import React, { useState, useContext, useEffect } from 'react';
import classes from './Profile.module.scss';
import AuthContext from '../../store/auth-context';
import { useToast } from '@chakra-ui/react';
import { refreshUserVerification } from '../../services';

const Referrals = () => {
  const { userData } = useContext(AuthContext);
  const [userInfo, setUserInfo] = useState(userData);
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const [canShare, setCanShare] = useState(false);

  // Check if Web Share API is available (mobile)
  useEffect(() => {
    setCanShare(navigator.share !== undefined);
  }, []);

  // Refresh user data on component mount to ensure we have latest affiliate info
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const refreshed = await refreshUserVerification();
        if (refreshed) {
          setUserInfo(refreshed);
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    };
    loadUserData();
  }, []);

  // Use the affiliateLink from userData if available, otherwise generate it
  const referralLink = userInfo?.affiliateLink || 
    (userInfo?.affiliateCode ? `${window.location.origin}/register?ref=${userInfo.affiliateCode}` : null);

  const copyToClipboard = async (text) => {
    if (!text) {
      toast({
        title: "Nothing to copy",
        description: "Data is still loading",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "Copied to clipboard!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const shareReferral = async () => {
    if (!referralLink || !canShare) return;

    const shareData = {
      title: 'Join me on Auction Master!',
      text: `Use my referral code: ${userInfo?.affiliateCode} or sign up with this link:`,
      url: referralLink
    };

    try {
      await navigator.share(shareData);
      toast({
        title: "Shared successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast({
          title: "Failed to share",
          description: "Please try again",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <div className={classes.accountCard}>
      <h3 className={classes.cardTitle}>Referral Program</h3>
      
      {/* Affiliate Code Section */}
      <div className={classes.referralSection}>
        <div className={classes.referralHeader}>
          <h4>Your Affiliate Code</h4>
          <p>Share this code with others to earn rewards when they sign up</p>
        </div>
        
        <div className={classes.codeBox}>
          <span className={classes.code}>{userInfo?.affiliateCode || 'Loading...'}</span>
          <button 
            className={classes.copyButton}
            onClick={() => copyToClipboard(userInfo?.affiliateCode)}
            disabled={!userInfo?.affiliateCode}
          >
            {copied ? (
              <>
                <i className="fas fa-check"></i>
                <span className={classes.mobileText}>Copied!</span>
              </>
            ) : (
              <>
                <i className="fas fa-copy"></i>
                <span className={classes.mobileText}>Copy Code</span>
              </>
            )}
          </button>
        </div>

        {/* Affiliate Link Section */}
        <div className={classes.referralHeader}>
          <h4>Your Referral Link</h4>
          <p>Share this link directly with potential users</p>
        </div>
        
        <div className={classes.codeBox}>
          <span className={classes.link}>{referralLink || 'Loading...'}</span>
          <div className={classes.actionButtons}>
            <button 
              className={classes.copyButton}
              onClick={() => copyToClipboard(referralLink)}
              disabled={!referralLink}
            >
              {copied ? (
                <>
                  <i className="fas fa-check"></i>
                  <span className={classes.mobileText}>Copied!</span>
                </>
              ) : (
                <>
                  <i className="fas fa-copy"></i>
                  <span className={classes.mobileText}>Copy Link</span>
                </>
              )}
            </button>
            {canShare && (
              <button 
                className={classes.shareButton}
                onClick={shareReferral}
                disabled={!referralLink}
              >
                <i className="fas fa-share-alt"></i>
                <span className={classes.mobileText}>Share</span>
              </button>
            )}
          </div>
        </div>

        {/* Statistics Section */}
        <div className={classes.statsSection}>
          <div className={classes.statCard}>
            <div className={classes.statValue}>{userInfo?.referralCount || 0}</div>
            <div className={classes.statLabel}>Total Referrals</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals; 